import { debounce } from '@toggle/helpers';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QUERY_DEBOUNCE_OPTS, QUERY_DELAY } from '~/config/constants';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { useRecentSearches } from '~/views/news/hooks/use-recent-searches/useRecentSearches';
import { useNewsSearchStore } from '~/views/news/stores/use-news-search-store/useNewsSearchStore';

import { CloseButton } from '../close-button/CloseButton';
import { SearchInput } from '../search-input/SearchInput';
import classnames from 'classnames';

import styles from './SearchBar.module.scss';

export const SearchBar = () => {
  const { t } = useTranslation('news');
  const { params, setQueryParams, deleteQueryParam } = useQueryParams();
  const queryParam = params[queryKeys.searchNews] ?? '';

  const setSearchParam = (value: string) => {
    setQueryParams({
      [queryKeys.searchNews]: value,
    });
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  const { clearSearch, setValue, value, isInputExpanded, setIsInputExpanded } =
    useNewsSearchStore(state => ({
      clearSearch: state.clearSearch,
      setValue: state.setInputValue,
      value: state.inputValue,
      isInputExpanded: state.isInputExpanded,
      setIsInputExpanded: state.setInputExpanded,
    }));

  const [isFocused, setIsFocused] = useState(false);

  const { addRecentSearch } = useRecentSearches();

  useEffect(() => {
    if (queryParam && !value) {
      searchInputRef.current?.focus();
    }
  }, []);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    updateSearchParamsDebounced(event.target.value);
  };

  const updateSearchParamsDebounced = useCallback(
    debounce(
      (value: string) => {
        updateSearchParams(value);
      },
      QUERY_DELAY,
      QUERY_DEBOUNCE_OPTS
    ),
    []
  );

  const handleInputClear = () => {
    clearSearch();
    deleteQueryParam(queryKeys.searchNews);
  };

  const handleClose = () => {
    setIsInputExpanded(false);
    setIsFocused(false);
    handleInputClear();
  };

  const handleFocus = () => {
    setIsInputExpanded(true);
    setIsFocused(true);
  };

  const updateSearchParams = (value: string) => {
    setSearchParam(value);
    addRecentSearch(value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (value) {
      updateSearchParams(value);
      searchInputRef.current?.blur();
    }
  };

  return (
    <>
      <div
        className={classnames(styles.searchBarRoot, {
          [styles.expanded]: isInputExpanded,
          [styles.collapsed]: !isInputExpanded && isFocused,
        })}
        data-testid="search-bar"
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          <SearchInput
            ref={searchInputRef}
            onChange={handleQueryChange}
            onClear={handleInputClear}
            onFocus={handleFocus}
            placeholder={
              isInputExpanded
                ? t('news:searchBar.expandedPlaceholder')
                : t('news:searchBar.collapsedPlaceholder')
            }
            value={value}
            showClearButton={isInputExpanded && !!value}
          />
        </form>
      </div>

      {isInputExpanded && <CloseButton onClick={handleClose} />}
    </>
  );
};
